import {
    ProjectionVentilation,
    GetProjectionDetailsPayload,
    ProjectionDetailsT,
} from './../helpers/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    BUDGET_YEAR_FIELD,
    PROJECTION_DETAIL_AMOUNT,
    PROJECTION_DETAIL_COMMENT,
} from 'routes/BudgetModule/config/constants';
import { getResourceSoftwareIdField } from 'routes/BudgetModule/helpers/budgetProposalHelpers';
import {
    HttpContentWithData,
    HttpResponse,
    RQAPICall,
    RQAPICallWithData,
} from 'store/APIClient';
import { dispatch } from 'store/store';
import {
    AtterrissageProjectionUpdateFieldT,
    ProjectionDetailQueryResult,
    UseGetProjectionDetailsParams,
    UseGetProjectionDetailsResponse,
    UseUpdateProjectionDetailsParams,
} from '../helpers/types';

/**
 * Hook to fetch atterrissage ProjectionDetails
 */
export const useGetAtterrissageProjectionDetails = ({
    budgetProposalId,
    resourceSoftwareId,
    expenseOrIncome,
    enabled,
}: UseGetProjectionDetailsParams): UseGetProjectionDetailsResponse => {
    const resourceFilter = resourceSoftwareId
        ? `?resource_software_id=${resourceSoftwareId}`
        : '';
    const endpoint = `budget_proposals/atterrissage_details/${budgetProposalId}/${expenseOrIncome}${resourceFilter}`;

    return useQuery({
        queryKey: [
            'atterrissage_projection_details',
            budgetProposalId,
            expenseOrIncome,
            resourceSoftwareId || '',
        ],
        queryFn: async () => {
            const getResponse =
                await RQAPICallWithData<GetProjectionDetailsPayload>({
                    endpoint,
                    method: 'GET',
                });
            return getResponse.content.data;
        },
        enabled,
        meta: {
            useGlobalOnError: true,
        },
    });
};

type ProjectionUpdateT = {
    mutate: (arg1: AtterrissageProjectionUpdateFieldT) => void;
};
/**
 * Hook to update the amount or comment of a atterrissage projection details
 */
export function useUpdateAtterrissageProjectionDetails({
    budgetProposal,
    expenseOrIncome,
    isDaf,
    dimensions,
}: UseUpdateProjectionDetailsParams): ProjectionUpdateT {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: ({
            field,
            value,
            resourceSoftwareId,
        }: AtterrissageProjectionUpdateFieldT): Promise<
            HttpResponse<HttpContentWithData<ProjectionDetailsT>>
        > =>
            RQAPICall({
                endpoint: `budget_proposals/atterrissage_details/`,
                method: 'POST',
                body: {
                    [field]: value,
                    budgetProposalId: budgetProposal.id,
                    year: budgetProposal.exercice,
                    resourceSoftwareId:
                        resourceSoftwareId ||
                        dimensions[
                            getResourceSoftwareIdField(budgetProposal.by)
                        ],
                    expenseIncome: expenseOrIncome,
                    dimensions,
                },
            }),

        onSuccess: (response, variables): void => {
            const { resourceSoftwareId } = variables;
            dispatch.vision.notifyUser({
                message: response.content.message,
                snackType: 'success',
            });
            queryClient.setQueryData(
                [
                    'atterrissage_projection_details',
                    budgetProposal.id,
                    expenseOrIncome,
                    // Setting an undefined as a queryKey doesn't seem to work.
                    // For this reason we are using an empty string instead.
                    isDaf
                        ? ''
                        : resourceSoftwareId ||
                          dimensions[
                              getResourceSoftwareIdField(budgetProposal.by)
                          ],
                ],
                (oldData: ProjectionDetailQueryResult) => ({
                    ...oldData,
                    data: oldData.data.map((od) => {
                        const isUpdated = Object.keys(dimensions).every(
                            (dimension: ProjectionVentilation.SoftwareIds) =>
                                od[dimension] === dimensions[dimension],
                        );
                        if (!isUpdated) return od;
                        return od[BUDGET_YEAR_FIELD] === budgetProposal.exercice
                            ? {
                                  ...od,
                                  [PROJECTION_DETAIL_AMOUNT]:
                                      response.content.data.amount,
                                  [PROJECTION_DETAIL_COMMENT]:
                                      response.content.data.comment,
                              }
                            : od;
                    }),
                }),
            );
        },

        meta: {
            useGlobalOnError: true,
        },
    });
    return mutation;
}
